// Chore bar: five icon rows in thumb reach, each showing Due / Done / Soon /
// the time it comes due (docs/ui/mockups 04-daily-coop).
//
// Six during a storm watch, when the tie-down chore joins the list, so the bar
// reflows to two rows of three rather than leaving the storm row alone on a
// line under five others.
//
// The states are the sim's own, read through `selectChoreRows`, which is the
// same dueChores/applicableChores pair the old bar uses. The bar is repainted
// through `paint()`, so a chip is only rebuilt when its state actually changes —
// replacing the nodes on every tick would detach a chip from under a thumb.

import { t } from '../../i18n';
import { button, h, icon } from '../dom';
import { CHORE_ICONS, type ChoreRowView } from '../selectors';

export interface ChoreBarOptions {
  rows: ChoreRowView[];
  onChore: (chore: ChoreRowView) => void;
}

export function choreBar(options: ChoreBarOptions): HTMLElement {
  const nav = h('nav', {
    class: `cf-chorebar${options.rows.length > 5 ? ' cf-chorebar--wrap' : ''}`,
    attrs: { 'aria-label': t('chore.bar.title') },
  });
  for (const row of options.rows) {
    const detail = row.detail ?? t(row.detailKey);
    const chip = button(
      {
        class: `cf-chorechip cf-chorechip--${row.tone}`,
        attrs: {
          // The status is a word, not only a colour (UI-DESIGN §6).
          'aria-label': `${row.label} — ${detail}`,
          'aria-pressed': String(row.status === 'done'),
          disabled: row.status === 'idle',
        },
        on: { click: () => options.onChore(row) },
      },
      icon({ shapes: CHORE_ICONS[row.id], size: 24, tone: iconTone(row.tone) }),
      h('span', { class: 'cf-chorechip__label', text: row.label }),
      h('span', { class: 'cf-chorechip__detail', text: detail }),
    );
    nav.appendChild(chip);
  }
  return nav;
}

function iconTone(tone: ChoreRowView['tone']): 'ink' | 'warn' | 'sage' | 'muted' {
  if (tone === 'good') return 'sage';
  if (tone === 'warn') return 'warn';
  if (tone === 'idle') return 'muted';
  return 'ink';
}

/** The state the bar draws, as a string: the key `paint()` repaints on. */
export function choreBarKey(rows: ChoreRowView[]): string {
  return rows
    .map((row) => `${row.id}:${row.status}:${row.detail ?? ''}:${row.tone}`)
    .join('|');
}
